import React, { useMemo, useState } from "react";
import moment from "moment";

const HorizontalTimeline = ({ verification = {}, visits = [] }) => {
  const [hoveredEvent, setHoveredEvent] = useState(null);

  const timelineEvents = useMemo(() => {
    const eventMap = {
      FechaCreacion: { label: "Creación", color: "#52c41a", category: "verification" },
      FechaListo: { label: "Listo", color: "#1890ff", category: "verification" },
      FechaEmision: { label: "Emisión", color: "#722ed1", category: "verification" },
      FechaPago: { label: "Pagado", color: "#52c41a", category: "verification" },
      FechaRevision: { label: "Revisión", color: "#fa8c16", category: "verification" },
      FechaPapeleria: { label: "Papelería", color: "#13c2c2", category: "verification" },
      FechaCancelacion: { label: "Cancelación", color: "#f5222d", category: "verification" },
      FechaRecepcion: { label: "Solicitud", color: "#eb2f96", category: "verification" },
      FechaContrato: { label: "Contrato", color: "#2f54eb", category: "verification" },
    };

    const verificationEvents = Object.entries(eventMap).map(([key, config]) => ({
      id: key,
      date: verification?.[key],
      hasDate: !!verification?.[key],
      ...config,
    }));

    const visitEvents = visits.map((visit, index) => ({
      id: `visit-${index}`,
      date: visit?.FechaVisita,
      hasDate: !!visit?.FechaVisita,
      label: `Visita ${index + 1}`,
      color: "#9254de",
      category: "visit",
    }));

    const allEvents = [...verificationEvents, ...visitEvents];

    return allEvents.sort((a, b) => {
      if (a.hasDate && b.hasDate) return moment(a.date, "YYYY-MM-DD").valueOf() - moment(b.date, "YYYY-MM-DD").valueOf();
      if (a.hasDate && !b.hasDate) return -1;
      if (!a.hasDate && b.hasDate) return 1;
      return 0;
    });
  }, [verification, visits]);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return moment(dateString, "YYYY-MM-DD").format("DD/MM/YYYY");
  };

  // This function now correctly centers the event container and its dot on the timeline
  const getEventStyle = (event, index) => {
    const baseStyle = {
      position: "absolute",
      left: `${(index / (timelineEvents.length - 1)) * 100}%`,
      top: "50%", // Position the container's top at the 50% mark of the parent
      transform: "translate(-50%, -50%)", // Then shift it up by 50% of its own height
      cursor: "pointer",
      transition: "all 0.3s ease",
      zIndex: 2, // Ensure the event container is above the timeline line
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    };

    // Adjustments for the first and last items to align them with the edges
    if (index === 0) {
      baseStyle.transform = "translate(0, -50%)";
    }
    if (index === timelineEvents.length - 1) {
      baseStyle.transform = "translate(-100%, -50%)";
    }

    return baseStyle;
  };

  if (timelineEvents.length === 0) {
    return null;
  }

  return (
    <div style={{ position: "relative", width: "100%", padding: "2.5rem 1rem" }}>
      {/* Timeline line container */}
      <div style={{ position: "relative", height: "24px" }}>
        {/* The line itself */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: 0,
            width: "100%",
            height: "3px",
            background: "linear-gradient(90deg, #e8e8e8 0%, #d9d9d9 100%)",
            transform: "translateY(-50%)",
            borderRadius: "2px",
            zIndex: 1, // Lower z-index so it's behind the dots
          }}
        />
        {/* Events are mapped here */}
        {timelineEvents.map((event, index) => (
          <div
            key={event.id}
            style={getEventStyle(event, index)}
            onMouseEnter={() => setHoveredEvent(event.id)}
            onMouseLeave={() => setHoveredEvent(null)}
          >
            {/* Label above dot */}
            <div style={{ fontSize: "11px", minWidth: "60px", fontWeight: "600", color: event.hasDate ? "#262626" : "#8c8c8c", marginBottom: "10px", textAlign: "center" }}>
              {event.label}
            </div>

            {/* Event dot - now uses boxShadow for a cleaner look */}
            <div
              style={{
                width: event.hasDate ? "16px" : "12px",
                height: event.hasDate ? "16px" : "12px",
                backgroundColor: event.hasDate ? event.color : "#d9d9d9",
                borderRadius: "50%",
                boxShadow: `0 0 0 3px white, ${event.hasDate ? "0 2px 8px rgba(0,0,0,0.15)" : "0 1px 4px rgba(0,0,0,0.1)"}`,
                transform: hoveredEvent === event.id ? "scale(1.2)" : "scale(1)",
                transition: "all 0.2s ease",
                opacity: event.hasDate ? 1 : 0.6,
                background: event.hasDate ? event.color : `repeating-linear-gradient(45deg, #d9d9d9, #d9d9d9 2px, #f0f0f0 2px, #f0f0f0 4px)`,
              }}
            />

            {/* Date below dot */}
            <div style={{ fontSize: "10px", minWidth: "60px", color: event.hasDate ? "#595959" : "#bfbfbf", marginTop: "10px", textAlign: "center" }}>
              {event.hasDate ? formatDate(event.date) : "No asignado"}
            </div>

            {/* Tooltip remains the same */}
            {hoveredEvent === event.id && (
              <div style={{ position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", marginBottom: "10px", backgroundColor: "rgba(0, 0, 0, 0.85)", color: "white", padding: "8px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: "500", whiteSpace: "nowrap", zIndex: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.2)", animation: "fadeIn 0.2s ease-in" }}>
                <div style={{ fontWeight: "bold", marginBottom: "2px" }}>{event.label}</div>
                <div style={{ opacity: 0.9 }}>{event.hasDate ? formatDate(event.date) : "Fecha sin asignar"}</div>
                <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid rgba(0, 0, 0, 0.85)" }} />
              </div>
            )}
          </div>
        ))}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translate(-50%, 5px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
};

export default HorizontalTimeline;